import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import config from '../config';

const Dashboard = () => {
  const [profileData, setProfileData] = useState({
    name: '',
    bookingsCount: 0,
    usersCount: 0,
    successfulPaymentsSum: 0,
    last30Days: 0
  });
  const [showCustomers, setShowCustomers] = useState(false);
  const [showRevenue, setShowRevenue] = useState(false);
  const [show30, setShow30] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const response = await fetch(`${config.apiBaseUrl}/api/admin/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch profile data');
        }

        const data = await response.json();
        setProfileData({
          name: data.name,
          bookingsCount: data.bookingsCount,
          usersCount: data.usersCount,
          successfulPaymentsSum: data.successfulPaymentsSum,
          last30Days: data.last30Days
        });
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };

    fetchProfileData();
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  return (
    <div className="flex min-h-screen bg-gray-800 text-white">
      <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
      
      <div className="flex-1 flex flex-col lg:ml-0">
        {/* Mobile Header */}
        <div className="lg:hidden bg-gray-900 p-4 flex justify-between items-center">
          <button 
            onClick={toggleSidebar}
            className="text-white hover:text-gray-300"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              strokeWidth={1.5} 
              stroke="currentColor" 
              className="w-6 h-6"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>
          <div className="bg-gray-700 px-3 py-2 rounded text-sm truncate max-w-40">
            {profileData.name}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 lg:p-6">
          {/* Desktop Header */}
          <div className="hidden lg:flex justify-end mb-4">
            <div className="bg-gray-700 px-4 py-2 rounded">{profileData.name}</div>
          </div>

          {/* Dashboard Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
            {/* Booking Card */}
            <div className="bg-white p-4 rounded-md flex flex-col items-center justify-center min-h-[160px] lg:min-h-[180px]">
              <div className="text-2xl lg:text-3xl text-blue-900 font-bold mb-3 lg:mb-5 text-center">
                Booking
              </div>
              <div className="text-2xl lg:text-3xl font-bold text-gray-900">
                {profileData.bookingsCount}
              </div>
            </div>

            {/* Customers Card */}
            <div className="bg-white p-4 rounded-md flex flex-col items-center justify-center min-h-[160px] lg:min-h-[180px]">
              <div className="text-2xl lg:text-3xl text-blue-900 font-bold mb-3 lg:mb-5 text-center">
                Customers
              </div>
              <div className="flex items-center gap-2">
                <div className="text-2xl lg:text-3xl font-bold text-gray-900">
                  {showCustomers ? profileData.usersCount : '*****'}
                </div>
                <button 
                  onClick={() => setShowCustomers(!showCustomers)}
                  className="text-gray-600 hover:text-gray-900 p-1"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    strokeWidth={1.5} 
                    stroke="currentColor" 
                    className="w-5 h-5 lg:w-6 lg:h-6"
                  >
                    {showCustomers ? (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    )}
                  </svg>
                </button>
              </div>
            </div>

            {/* Total Revenue Card */}
            <div className="bg-white p-4 rounded-md flex flex-col items-center justify-center min-h-[160px] lg:min-h-[180px]">
              <div className="text-2xl lg:text-3xl text-blue-900 font-bold mb-3 lg:mb-5 text-center">
                Total Revenue
              </div>
              <div className="flex items-center gap-2">
                <div className="text-2xl lg:text-3xl font-bold text-gray-900">
                  {showRevenue ? profileData.successfulPaymentsSum : '*****'}
                </div>
                <button 
                  onClick={() => setShowRevenue(!showRevenue)}
                  className="text-gray-600 hover:text-gray-900 p-1"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    strokeWidth={1.5} 
                    stroke="currentColor" 
                    className="w-5 h-5 lg:w-6 lg:h-6"
                  >
                    {showRevenue ? (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    )}
                  </svg>
                </button>
              </div>
            </div>

            {/* Current Month Card */}
            <div className="bg-white p-4 rounded-md flex flex-col items-center justify-center min-h-[160px] lg:min-h-[180px]">
              <div className="text-2xl lg:text-3xl text-blue-900 font-bold mb-3 lg:mb-5 text-center">
                Current Month
              </div>
              <div className="flex items-center gap-2">
                <div className="text-2xl lg:text-3xl font-bold text-gray-900">
                  {show30 ? profileData.last30Days : '*****'}
                </div>
                <button 
                  onClick={() => setShow30(!show30)}
                  className="text-gray-600 hover:text-gray-900 p-1"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    strokeWidth={1.5} 
                    stroke="currentColor" 
                    className="w-5 h-5 lg:w-6 lg:h-6"
                  >
                    {show30 ? (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    )}
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;